import { ComponentType, useCallback } from 'react';
import { ErrorBoundary, type FallbackProps } from 'react-error-boundary';
import { useTrackComponent } from 'src/contexts/AppPerformanceContext';
import { useClientLogContext } from 'src/contexts/ClientLogContext';
import { PosSpinner } from 'src/core/POS/PosSpinner';

/**
 * Higher-order component using ErrorBoundary for automatic error tracking.
 * This version allows the use of custom hooks inside the wrapped component.
 */
export const withErrorBoundary = <P extends object>(
  WrappedComponent: ComponentType<P>,
  options?: {
    componentName?: string;
    fallback?: React.ComponentType<FallbackProps>;
    onError?: (error: Error) => void;
  }
) => {
  const componentName =
    options?.componentName ||
    WrappedComponent.displayName ||
    WrappedComponent.name ||
    'Component';

  const FallbackComponent =
    options?.fallback || ((_: FallbackProps) => <PosSpinner />);

  // Return a functional component that can use hooks
  function ComponentWithErrorBoundary(props: P) {
    useTrackComponent(componentName);
    const { showErrorDialog } = useClientLogContext();
    const onError = useCallback(
      (error: Error) => {
        if (options?.onError) {
          return options.onError(error);
        }
        showErrorDialog(`Error-${componentName}`, error, {
          customerHeader: `Unhandled error in ${componentName}`,
        });
      },
      [showErrorDialog]
    );
    return (
      <ErrorBoundary FallbackComponent={FallbackComponent} onError={onError}>
        <WrappedComponent {...props} />
      </ErrorBoundary>
    );
  }

  ComponentWithErrorBoundary.displayName = `withErrorBoundary(${componentName})`;

  return ComponentWithErrorBoundary;
};
